<template>
<div class="page-content">
  <h1><a rel="noopener" target="_blank" style="color: inherit;" :href="'/api/_id/'+panel._id">{{ panel.panel_index }}</a></h1>
  <div class="subtitle">
    <b-badge :variant="statusBadge(panel.status)">{{ panel.status }}</b-badge>
    <b-badge class="badge" v-if="lastData.hacked" variant="danger"> hacked </b-badge>
  </div>
  <div v-if="lastData.bot_total !== undefined">
    <div class="row hl-cards">
      <div class="col-md">
        <div class="card text-white bg-primary">
          <div class="card-body">
            <div class="card-body hl-card">
              <div class="hl-card__data">{{ formatInteger(lastData.bot_total) }}</div>
              <div class="hl-card__text">victims</div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md">
        <div class="card text-white bg-primary">
          <div class="card-body">
            <div class="card-body hl-card">
              <div class="hl-card__data">{{ formatInteger(lastData.password_total) }}</div>
              <div class="hl-card__text">stolen passwords</div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md">
        <div class="card text-white bg-primary">
          <div class="card-body card-body-nopadx">
            <div class="card-body hl-card">
              <template v-if="topCountries.length">
                <div class="hl-card__data">
                  <div class="hl-card__country" v-for="country in topCountries" :key="country">
                    <country-flag :country='country' size='big' v-b-tooltip.hover :title='countryTooltip(country)' />
                  </div>
                </div>
                <div class="hl-card__text">main victim countries</div>
              </template>
              <template v-else>
                <div class="hl-card__data">0</div>
                <div class="hl-card__text">country targeted</div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div>
    <div class="row">
      <div v-if="panel.data" class="col-md">
        <div class="card text-white bg-primary">
          <div class="card-header">Information</div>
          <div class="card-body card-body-nopad server-info-body">
            <VerticalTable :items="infoTable">
              <template slot="Country" scope="data">
                <a :href="'/s/country/' + data.value">
                  <country-flag :country='data.value' v-b-tooltip.hover :title='countryName(data.value)' />
                </a>
              </template>
              <template slot="IP" scope="data">
                <a :href="'/s/ip/' + data.value">{{ data.value }}</a>
              </template>
              <template slot="Domain" scope="data">
                <a :href="'/s/FQDN/' + data.value">{{ data.value }}</a>
              </template>
              <template slot="AS" scope="data">
                <a :href="'/s/asn/' + data.value">{{ data.value }}</a>
              </template>
              <template slot="AS Organisation" scope="data">
                {{ data.value }}
              </template>
              <template slot="Panel version" scope="data">
                {{ data.value }}
              </template>
              <template slot="Feeder" scope="data">
                {{ $const.feederNames[data.value] }}
              </template>
            </VerticalTable>
          </div>
        </div>
      </div>
      <div v-if="lastData.bot_total !== undefined" class="col-md">
        <div class="card text-white bg-primary">
          <div class="card-header">
            Infected countries
            <b-form-select v-model="selectedCountry" :options="selectOptions" class="select-cn" size="sm"></b-form-select>
          </div>
          <div class="card-body">
            <CountryGraph class="country-graph" :data="panel" :country="selectedCountry" :style="getGraphHeight()" />
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-if="panel.data">
    <div v-if="config.length || loaderEvolution.length" class="config">
      <div v-if="config.length" class="card text-white bg-primary">
        <div class="card-header">Settings</div>
        <div class="card-body">
          <b-badge v-for="conf in config" :key="conf.key" class="badge" :variant="boolBadge(conf.value)" v-b-tooltip.hover :title='$const.settingsDescription[conf.key]'>{{ conf.key }}</b-badge>
        </div>
      </div>
      <div v-if="loaderEvolution.length" class="card text-white bg-primary">
        <div class="card-header">Loaders evolutions</div>
        <div class="card-body">
          <div v-for="(evolution, index) in loaderEvolution" :key="`evolution-${index}`">
            <p class="date-title date-loaders"><i class="icon-clock"></i> {{ evolution.date }}</p>
            <b-table responsive striped hover :items="evolution.loader" :fields="loaderEvolutionFields">
              <template v-slot:cell(ldIndex)="data">{{data.index+1}}</template>
              <template v-slot:cell(ldLink)="data">
                <a rel="noopener" target="_blank" :href="`https://www.virustotal.com/latest-scan/${data.value}`">{{ data.value }}</a>
              </template>
              <template v-slot:cell(sha256)="data">
                <a rel="noopener" target="_blank" style="word-wrap: anywhere; min-width: 250px; display: inline-block;" :href="`https://www.virustotal.com/#/search/${data.value}`">{{ data.value }}</a>
              </template>
            </b-table>
          </div>
        </div>
      </div>
      <div v-if="configFiles.length" class="card text-white bg-primary">
        <div class="card-header">Files grabber</div>
        <div class="card-body card-body-nopad">
          <b-table responsive striped hover :items="configFiles" :fields="fileConfigFields"></b-table>
        </div>
      </div>
      <div v-if="dataLinks.length" class="card text-white bg-primary">
        <div class="card-header">Important links</div>
        <div class="card-body">{{dataLinks}}</div>
      </div>
    </div>
    <div v-if="lastData.software && Object.entries(lastData.software).length" class="card text-white bg-primary">
      <div class="card-header">Stolen passwords</div>
      <div class="card-body">
        <template v-for="(number, software) in lastData.software">
          <b-badge class="badge-embedded" :variant="typeBadge($const.softwares[software].type)" :key="software">
            {{ $const.softwares[software].name }}
            <b-badge variant="light">{{ formatInteger(number) }}</b-badge>
          </b-badge>
        </template>
      </div>
    </div>
    <div v-if="lastData.bot_total" class="card text-white bg-primary">
      <div class="card-header">Infected machines</div>
      <div class="card-body card-body-nopad">
        <VerticalTable :items="machineTable">
          <template slot="Architecture" scope="data">
            <template v-for="(value, key) in data.value">
              <b-badge class="badge-embedded" variant="azorult" :key="key">
                {{ key }}
                <b-badge variant="light">{{ formatInteger(value) }}</b-badge>
              </b-badge>
            </template>
          </template>
          <template slot="OS Version" scope="data">
            <template v-for="(value, key) in data.value">
              <b-badge class="badge-embedded" variant="azorult" :key="key">
                {{ key }}
                <b-badge variant="light">{{ formatInteger(value) }}</b-badge>
              </b-badge>
            </template>
          </template>
          <template slot="Rights" scope="data">
            <template v-for="(value, key) in data.value">
              <b-badge class="badge-embedded" variant="azorult" :key="key">
                {{ key }}
                <b-badge variant="light">{{ formatInteger(value) }}</b-badge>
              </b-badge>
            </template>
          </template>
          <template slot="Binary Type" scope="data">
            <template v-for="(value, key) in data.value">
              <b-badge class="badge-embedded" variant="azorult" :key="key">
                {{ key }}
                <b-badge variant="light">{{ formatInteger(value) }}</b-badge>
              </b-badge>
            </template>
          </template>
        </VerticalTable>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import CountryFlag from 'vue-country-flag'
import CountryGraph from '@/components/CountryGraph.vue'
import VerticalTable from '@/components/VerticalTable.vue'

export default {
  components: {
    CountryFlag,
    CountryGraph,
    VerticalTable
  },
  data() {
    return {
      selectedCountry: '',
      panel: {},
      config: [],
      configFiles: [],
      dataLinks: [],
      lastData: {},
      loaderEvolution: [],
      infoFields: [{
          id: 0,
          label: 'Key',
          sortable: true
        },
        {
          id: 1,
          label: 'Value',
          sortable: true
        },
      ],
      fileConfigFields: [{
          key: 'fgName',
          label: 'Name',
          class: 'd-none d-md-table-cell',
          sortable: true
        },
        {
          key: 'fgPath',
          label: 'Path',
          class: 'd-none d-lg-table-cell',
          sortable: true
        },
        {
          key: 'fgMask',
          label: 'Mask',
          class: 'col-mask',
          sortable: true
        },
        {
          key: 'fgMaxSize',
          label: 'Max size',
          class: 'd-none d-lg-table-cell',
          sortable: true
        },
        {
          key: 'fgSubfolders',
          label: 'Subfolders',
          class: 'd-none d-md-table-cell',
          sortable: true
        },
        {
          key: 'fgShortcuts',
          label: 'Shortcuts',
          class: 'd-none d-md-table-cell',
          sortable: true
        },
        {
          key: 'fgExceptions',
          label: 'Exceptions',
          class: 'd-none d-lg-table-cell',
          sortable: true
        },
      ],
      loaderEvolutionFields: [
        // { key: 'ldIndex', label: 'Index', thStyle: { width: '50px'}},
        {
          key: 'ldLink',
          label: 'URL',
          thStyle: {
            width: '400px'
          },
          tdStyle: {
            "word-wrap": "anywhere"
          }
        },
        {
          key: 'ldHide',
          label: 'Hide',
          thStyle: {
            width: '75px'
          },
          class: 'd-none d-lg-table-cell'
        },
        {
          key: 'ldTags',
          label: 'Tags',
          thStyle: {
            width: '100px'
          },
          class: 'd-none d-lg-table-cell'
        },
        {
          key: 'sha256',
          label: 'SHA256',
          thStyle: {
            width: '350px'
          },
          class: 'd-none d-md-table-cell'
        },
        {
          key: 'type',
          label: 'Type',
          thStyle: {
            width: '75px'
          },
          class: 'd-none d-md-table-cell'
        },
      ]
    }
  },
  mounted() {
    if (!this.$route.params.id) {
      this.$router.push('/')
      return
    }
    this.$API.getId(this.$route.params.id).then(panels => {
      if (!panels.length) {
        this.$router.push('/')
        return
      }
      this.panel = panels[0]
      this.loaderEvolution = this.getLoaderEvolution(this.panel)
      this.lastData = this.getLastData(this.panel)
      this.lastDataConfig = this.getLastDataConfig(this.panel)
      if ("important_links" in this.lastData) {
        this.dataLinks = this.lastData["important_links"];
      }
      if (this.lastDataConfig) {
        this.config = this.getConfig(this.lastDataConfig)
        this.configFiles = this.getConfigFiles(this.lastDataConfig)
      }
    })
  },
  methods: {
    boolBadge(bool) {
      return {
        "true": "success",
        "false": "danger"
      } [bool]
    },
    statusBadge(status) {
      return {
        "online": "success",
        "offline": "danger",
        "waiting": "secondary"
      } [status]
    },
    typeBadge(type) {
      return {
        "web": "success",
        "mail": "darkblue",
        "ftp": "info",
        "im": "warning"
      } [type]
    },
    formatInteger(integer) {
      return integer.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
    },
    countryTooltip(country) {
      return `${this.$const.countries[country.toLowerCase()].name}: ${this.lastData.country[country]}`
    },
    countryName(country) {
      return country in this.$const.countries
          ? this.$const.countries[country.toLowerCase()].name
          : ''
    },
    getLastData(panel) {
      let lastData = {}
      for (let i = panel.data.length - 1; i >= 0; i--) {
        if (panel.data[i].bot_total !== undefined) { // Last time we got "bot_total"
          lastData = panel.data[i]
          break
        }
      }
      return lastData
    },
    getLastDataConfig(panel) {
      let lastConfig = {}
      for (let i = panel.data.length - 1; i >= 0; i--) {
        if (panel.data[i].config !== undefined) {
          lastConfig = panel.data[i]
          break
        }
      }
      return lastConfig
    },
    getConfig(data) {
      let config = Object.assign({}, data.config)
      delete config.files
      delete config.loader
      return Object.entries(config).map(
        ([key, value]) => ({
          key,
          value
        })
      )
    },
    getConfigFiles(data) {
      return data.config ? Object.values(data.config.files) : []
    },
    getLoaderEvolution(panel) {
      let loaders = []
      let previousLoader = []
      for (let i = 0; i < panel.data.length; i++) {
        if (panel.data[i].config === undefined) {
          continue
        }
        let loader = Object.values(panel.data[i].config.loader)
        /*.sort(
                            (a, b) => a.md5 > b.md5
                        )*/
        if (!this.areLoaderDifferent(loader, previousLoader)) {
          continue
        }
        /*loader["ldIndex"] = j;*/
        loaders.push({
          date: this.$moment(panel.data[i].date * 1000).format('DD-MM-YYYY HH:mm'),
          loader: loader,
        })
        previousLoader = loader
      }
      return loaders
    },
    areLoaderDifferent(loader1, loader2) {
      if (!loader1 || !loader2 || loader1.length !== loader2.length) {
        return true
      }
      for (let i = 0; i < loader1.length; i++) {
        if (loader1[i]['type'] === 'html') {
          continue
        }
        for (let key in loader1[i]) {
          if (loader1[i][key] !== loader2[i][key]) {
            return true
          }
        }
      }
      return false
    },
    getGraphHeight() {
      if (this.panel.status == "offline") {
        return {
          "height": "514px"
        }
      }
      return {
        "height": "465px"
      }
    },
  },
  computed: {
    infoTable() {
      let first_seen = this.$moment(this.panel.first_seen * 1000).format('YYYY-MM-DD HH:mm')
      let table = [{
        key: 'First seen',
        value: first_seen
      }]
      if (this.panel.data.length) {
        let last_scan_raw = this.panel.data.slice(-1)[0].date
        let last_scan = this.$moment(last_scan_raw * 1000).format('YYYY-MM-DD HH:mm')
        table.push({
          key: 'Last scan',
          value: last_scan
        })
      }
      if (this.panel.status == "offline") {
        let last_scan_raw = this.panel.data.slice(-1)[0].date
        let last_scan = this.$moment(last_scan_raw * 1000).format('YYYY-MM-DD HH:mm')
        let lastp = {
          key: 'Offline since',
          value: last_scan  // Default Offline = last_scan
        }
        let laston = false
        for (var k in this.panel.data) {
          if (laston) {
            let sincedate_raw = this.panel.data[k]["date"]
            let sincedate = this.$moment(sincedate_raw * 1000).format('YYYY-MM-DD HH:mm')
            lastp = {
              key: 'Offline since',
              value: sincedate
            }
            laston = false
          }
          if ("config" in this.panel.data[k]) {
            laston = true
          }
        }
        table.push(lastp)
      }
      let feeder = this.panel.feeder;

      if (this.panel.domain !== undefined) {
        table.push({
          key: 'FQDN',
          value: this.panel.domain
        })
      }
      table.push({
        key: 'IP',
        value: this.panel.ip
      }, {
        key: 'AS',
        value: this.panel.asn
      }, {
        key: 'AS Organisation',
        value: this.panel.as_org
      }, {
        key: 'Country',
        value: this.panel.country_code
      }, {
        key: 'Panel version',
        value: this.panel.panel_version
      }, {
        key: 'Feeder',
        value: feeder
      })
      if (this.panel.panel_path !== '') {
        let panel_root = this.panel.panel_index.substr(0, this.panel.panel_index.lastIndexOf('/'));
        let panel_admin = `${panel_root}${this.panel.panel_path}admin.php`
        table.push({
          key: 'Admin URL',
          value: panel_admin
        })
      }
      return table
    },
    machineTable() {
      let osType = {}
      for (let os in this.lastData.os) {
        let osName = {
          '5.1': 'Windows XP',
          '5.2': 'Windows XP 64-Bit',
          '5.2s': 'Windows Server 2003',
          '6.0': 'Windows Vista',
          '6.0s': 'Windows Server 2008',
          '6.1': 'Windows 7',
          '6.1s': 'Windows Server 2008 R2',
          '6.2': 'Windows 8',
          '6.2s': 'Windows Server 2012',
          '6.3': 'Windows 8.1',
          '6.3s': 'Windows Server 2012 R2',
          '10.0': 'Windows 10',
          '10.0s': 'Windows Server 2016'
        } [os]
        osType[osName] = this.lastData.os[os]
      }
      let rights = {}
      for (let right in this.lastData.right) {
        let rightName = {
          'u': 'User',
          'a': 'Admin',
          's': 'System',
          'g': 'Guest'
        } [right]
        rights[rightName] = this.lastData.right[right]
      }
      let binaryTypes = {}
      for (let type in this.lastData.binary) {
        let typeName = {
          'e': 'exe',
          'd': 'dll'
        } [type]
        binaryTypes[typeName] = this.lastData.binary[type]
      }
      let table = [{
          key: 'Architecture',
          value: this.lastData.arch
        },
        {
          key: 'OS Version',
          value: osType
        },
        {
          key: 'Rights',
          value: rights
        },
        {
          key: 'Binary Type',
          value: binaryTypes
        },
      ]
      return table
    },
    topCountries() {
      let topCountries = []
      if (this.lastData && this.lastData.country) {
        Object.keys(this.lastData.country).forEach(country => {
          if (country !== 'aa') {
            topCountries.push(country)
          }
        })
      }
      return topCountries.slice(0, 3)
    },
    selectOptions() {
      let countries = new Set()
      this.panel.data.forEach(data => {
        if (data.country) {
          for (let country in data.country) {
            countries.add(country)
          }
        }
      })
      countries = Array.from(countries)
      let options = Object.entries(this.lastData.country).map(([country, n]) => {
        if (countries.includes(country)) {
          return {
            value: country,
            text: `${this.$const.countries[country].name} (${n})`
          }
        }
      })
      options.unshift({
        value: '',
        text: `World (${this.lastData.bot_total})`
      })
      return options
    }
  }
}
</script>

<style lang="scss">
.col-mask {
    max-width: 350px;
}
</style>

<style lang="scss" scoped>
h1 {
    text-align: center;
}

h3 {
    margin: 10px 0 20px;
}

.subtitle {
    text-align: center;
    margin-bottom: 20px;
    font-size: 20px;
}

.hl-cards {
    .card {
        height: 170px;
    }
}

.hl-card {
    text-align: center;

    &__data {
        font-size: 28px;
    }
    &__country {
        display: inline-block;
        margin: -5px 5px;
    }
}

.card {
    margin-bottom: 10px;
}
.date-title {
    color: #ab9b9b;

    i {
        margin-right: 4px;
    }
}

.server-info-body {
    overflow-x: hidden;
}

.select-cn {
    position: absolute;
    right: 10px;
    top: 9px;
    width: 200px;
}

.badge {
    margin-right: 3px;

    &-azorult {
        background: #777070;
    }

    &-darkblue {
        background: #337AB7;
    }
}

.badge-embedded {
    font-size: 13px;
}
.table-responsive {
    margin-bottom: 0;
}
</style>
